import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DropResult } from "@hello-pangea/dnd";
import { okrService } from "@/services/okrService";
import { useToast } from "@/hooks/use-toast";
import { krListParams } from "@/hooks/useKeyResults";
import { OKR_ITEMS_PER_PAGE } from "@/hooks/useOkrPageState";
import { reorderPermutation } from "@/lib/reorder";
import { okrErrorDescription } from "@/lib/okr-errors";
import { KR_DROPPABLE_PREFIX } from "@/components/okr/okr-tree-constants";
import { KeyResult, Objective, ObjectiveFilter, PaginatedResponse } from "@/types";

interface Args {
  objectives: Objective[];
  params: ObjectiveFilter;
  page: number;
}

/**
 * One drag handler for the OKR tree. `result.type` tells which level moved;
 * both paths write the optimistic order into the cache, then persist the
 * full permutation.
 */
export function useOkrReorder({ objectives, params, page }: Args) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const objectiveMutation = useMutation({
    mutationFn: okrService.reorderObjectives,
    onSuccess: () => toast({ title: "Order updated", description: "Objective order saved." }),
    onError: (error) =>
      toast({ title: "Error", description: okrErrorDescription(error, "Failed to reorder objectives."), variant: "destructive" }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["objectives"] }),
  });

  const krMutation = useMutation({
    mutationFn: okrService.reorderKeyResults,
    onSuccess: () => toast({ title: "Order updated", description: "Key result order saved." }),
    onError: (error) =>
      toast({ title: "Error", description: okrErrorDescription(error, "Failed to reorder key results."), variant: "destructive" }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["key-results"] });
      queryClient.invalidateQueries({ queryKey: ["objectives"] });
    },
  });

  const onDragEnd = useCallback((result: DropResult) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;

    if (result.type === "KR") {
      // KR lists share a droppable type; a drop into another objective's list is not a reorder.
      if (result.source.droppableId !== result.destination.droppableId) return;
      const objectivePid = result.source.droppableId.slice(KR_DROPPABLE_PREFIX.length);
      const key = ["key-results", krListParams(objectivePid)];
      const list = queryClient.getQueryData<PaginatedResponse<KeyResult>>(key)?.data ?? [];
      if (!list.length) return;
      const { next, items } = reorderPermutation(list, from, to);
      queryClient.cancelQueries({ queryKey: key });
      queryClient.setQueryData<PaginatedResponse<KeyResult>>(key, (old) => old && { ...old, data: next });
      krMutation.mutate(items);
      return;
    }

    // ponytail: page-local permutation; indexes can collide across pages when
    // objectives exceed one page. Upgrade path: server-side renumber endpoint.
    const { next, items } = reorderPermutation(objectives, from, to, (page - 1) * OKR_ITEMS_PER_PAGE);
    queryClient.cancelQueries({ queryKey: ["objectives"] });
    queryClient.setQueryData<PaginatedResponse<Objective>>(["objectives", params], (old) => old && { ...old, data: next });
    objectiveMutation.mutate(items);
  }, [objectives, params, page, queryClient, objectiveMutation, krMutation]);

  // Drag stays off while a permutation is in flight so an older response cannot land last.
  return { onDragEnd, isPending: objectiveMutation.isPending || krMutation.isPending };
}
